import clsx from "clsx";
import React, { RefObject, useCallback } from "react";
import { useSelector } from "react-redux";
import { Breadcrumbs } from "web/react/components/breadcrumbs/breadcrumbs";
import { FeedHeaderImage } from "web/react/components/feed-header-image/feed-header-image";
import { FeedHeaderTitle } from "web/react/components/feed-header-title/feed-header-title";
import FollowDesignerButton from "web/react/components/follow-designer-button/follow-designer-button";
import { PromotionSummary } from "web/react/components/promotion-summary/promotion-summary";
import { Promotion } from "web/react/components/promotion/promotion";
import { StorefrontDescription } from "web/react/components/storefront-description/storefront-description";
import { StorefrontInfoModal } from "web/react/components/storefront-info-modal";
import { HStack } from "web/react/emo/h-stack";
import { Link } from "web/react/emo/link";
import { Sticker } from "web/react/emo/sticker";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useFeedContext } from "web/react/pages/feed/feed.context";
import { FeedPage, FeedPageType } from "web/react/pages/feed/utils";
import { ReduxStoreState } from "web/redux/store";
import { gettext } from "web/script/modules/django-i18n";
import { getLanguage } from "web/script/modules/language";
import { canUseMembership } from "web/script/modules/utils";
import {
    BreadcrumbsSerializer,
    FeedHeaderSerializer,
    FeedPromotionsSerializer,
    RetailPromotionSerializer,
} from "web/types/serializers";
import * as styles from "./storefront-feed-header.css";

export interface StorefrontFeedHeaderProps {
    promotions?: FeedPromotionsSerializer;
    title: string;
    subtitle?: string | JSX.Element;
    profile: FeedHeaderSerializer;
    shortDescription?: string | null;
    description?: string | null;
    followCTARef?: RefObject<HTMLDivElement>;
    actions?: JSX.Element;
    type?: FeedPageType;
    breadcrumbs?: BreadcrumbsSerializer | null;
}

export function StorefrontFeedHeader({
    promotions,
    profile,
    title,
    subtitle,
    shortDescription,
    description,
    followCTARef,
    actions,
    type = FeedPage.GENERIC_SHOP,
    breadcrumbs,
}: StorefrontFeedHeaderProps): JSX.Element {
    const { isMobileViewport, isTabletViewport, isDesktopViewport } = useDomViewport();
    const { isInformationModalOpen, setIsInformationModalOpen } = useFeedContext();
    const isVerified = profile?.checkout_verified || false;
    const language = getLanguage();
    const headerImageSrc = (profile.photo_url || profile.small_photo_url) ?? undefined;
    const headerImageAltText = profile.photo_alt_text ?? undefined;
    const designerName = profile.follow_button?.designer_name ?? undefined;

    const feedBreadcrumbsState = useSelector((state: ReduxStoreState) => state.feedBreadcrumbs);
    const feedBreadcrumbs = feedBreadcrumbsState?.breadcrumbs
        ? feedBreadcrumbsState.breadcrumbs
        : breadcrumbs?.breadcrumbs;

    const sanitizedDescription = (description || shortDescription)
        ?.replaceAll("<p>", "")
        .replaceAll("</p>", "");

    const promotionDescription = profile &&
        (profile.promotion as RetailPromotionSerializer) &&
        profile.promotion?.promo_is_valid && (
            <Sticker
                name="promotionCode"
                label={
                    <span
                        dangerouslySetInnerHTML={{
                            __html: profile.promotion.description,
                        }}
                    />
                }
            />
        );

    const storefrontAnalytics = {
        clicked: [
            {
                retailer_network: profile?.designer_name,
            },
        ],
        impression: [
            {
                retailer_network: profile?.designer_name,
            },
            {
                parent_area: "store_page",
            },
        ],
    };

    const onDescriptionClick = useCallback(() => {
        setIsInformationModalOpen(!isInformationModalOpen);
    }, [isInformationModalOpen, setIsInformationModalOpen]);

    function renderPromoHeader(): any {
        const activePromotions = promotions?.active_promotions;

        if (!activePromotions || activePromotions.length === 0) {
            return null;
        }

        if (activePromotions.length === 1) {
            const promo = activePromotions[0];

            return (
                <div className={styles.wideCardPromo}>
                    <Promotion
                        pageType="storefront"
                        message={promo.description}
                        promoCode={promo.discount_code}
                        expirationDateTimestamp={promo.expiration_date}
                        promoAnalyticsItemData={storefrontAnalytics}
                        isActive={true}
                    />
                </div>
            );
        }

        return <PromotionSummary promotions={promotions} />;
    }

    return (
        <>
            <StorefrontInfoModal
                isOpen={isInformationModalOpen}
                onClose={() => setIsInformationModalOpen(false)}
                title={title}
                dateAdded={profile.date_added || ""}
                description={sanitizedDescription}
                isCheckout={profile.checkout_verified}
                isOfficialStore={false} // TODO BEAM-998 https://lyst.atlassian.net/browse/BEAM-998
                bestShippingOption={profile.best_shipping_option || ""}
                shipsToCountry={profile.ships_to_country || ""}
            />
            <View>
                <div
                    className={clsx(styles.breadcrumbs, {
                        [styles.noBreadcrumbs]: !feedBreadcrumbs,
                    })}
                >
                    {feedBreadcrumbs && <Breadcrumbs type="feed" breadcrumbs={feedBreadcrumbs} />}
                </div>
                <div className={styles.wrapper} ref={followCTARef}>
                    <View className={styles.title}>
                        <HStack spacing={isMobileViewport ? "sm" : "md"}>
                            <FeedHeaderImage
                                src={headerImageSrc}
                                designerName={designerName}
                                altText={headerImageAltText}
                            />
                            <VStack spacing="xxs">
                                <FeedHeaderTitle
                                    title={title}
                                    subtitle={subtitle}
                                    type={type}
                                    showVerifiedTick={isVerified}
                                />

                                {actions && (
                                    <div className={styles.followButtonWrapper}>{actions}</div>
                                )}
                                {profile && (
                                    <div>
                                        {canUseMembership() && (
                                            <div className={styles.followButtonWrapper}>
                                                <HStack spacing={"xs"} align="center">
                                                    {profile.follow_button && (
                                                        <FollowDesignerButton
                                                            notFollowedText={gettext(
                                                                "feed.store_updates.cta"
                                                            )}
                                                            followedText={gettext(
                                                                "feed.following_store_or_brand.label"
                                                            )}
                                                            analyticsParameters={[
                                                                "du_feed_cta",
                                                                "click_top_cta",
                                                                "du_cta",
                                                            ]}
                                                            lystId={profile.follow_button.lyst_id}
                                                            isAuthenticated={
                                                                profile.follow_button
                                                                    .is_authenticated
                                                            }
                                                            isFollowing={
                                                                profile.follow_button
                                                                    .is_following as string
                                                            }
                                                            designerName={title}
                                                        />
                                                    )}
                                                    {!!profile.followers_count_label && (
                                                        <Text
                                                            as="span"
                                                            textStyle="body-s"
                                                            color="secondary"
                                                        >
                                                            {profile.followers_count_label}
                                                        </Text>
                                                    )}
                                                    {profile.retailer_hub_link && (
                                                        <Link
                                                            icon="tag-outline"
                                                            href={profile.retailer_hub_link}
                                                            title={
                                                                // "Promo’s"
                                                                gettext(
                                                                    "promotion_message.designer.promo_btn_text"
                                                                )
                                                            }
                                                            dataTestId="promo-link"
                                                        />
                                                    )}
                                                    {isDesktopViewport && promotionDescription && (
                                                        <View>{promotionDescription}</View>
                                                    )}
                                                </HStack>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </VStack>
                        </HStack>
                        {(isMobileViewport || isTabletViewport) && promotionDescription && (
                            <View paddingY={"xs"}>{promotionDescription}</View>
                        )}
                    </View>
                    {language.includes("en") && (
                        <div className={styles.description}>
                            <StorefrontDescription
                                description={sanitizedDescription}
                                beforeOnClick={onDescriptionClick}
                            />
                        </div>
                    )}
                    {(isTabletViewport || isDesktopViewport) && renderPromoHeader()}
                </div>
            </View>
        </>
    );
}
